import { message, StepProps } from 'antd';
import { makeAutoObservable, runInAction } from 'mobx';
import React from 'react';
import NewTicketTaskItem from '../../components/ticket/new-ticket-task-item';
import NewTicketTaskClonesItem from '../../components/ticket/new-ticket-task-item-clones';
import Subjects from '../../models/subjects';
import ITask from '../../models/task';
import TicketTypes from '../../models/ticket-type';
import DictionariesService from '../../services/dictionaries';
import FilesService from '../../services/files-service';
import { TaskService } from '../../services/task-service';
import { FinishTicketRequest, NewTicketRequest, TicketService } from '../../services/ticket-service';
import TaskItemStore from '../task/task-item';
import TicketBar from './ticket-bar';

export default class NewTicket {

    subject: Subjects;
    taskService: TaskService;
    filesService: FilesService;
    ticketService: TicketService;
    dictionariesService: DictionariesService;

    id?: number | null;
    ticketType: TicketTypes;
    taskCount: number;
    thematicTaskCount: number;
    name: string;
    egeTaskCount: number;
    currentTaskCount: number;
    tasks: ITask[];
    loading: boolean;
    pdfLink: string | null;

    parentId: number | null;
    parentTasks: TaskItemStore[];
    checkedParentTaskUuids: Set<string>;

    constructor(subject: Subjects) {

        this.subject = subject;
        this.taskService = new TaskService();
        this.filesService = new FilesService();
        this.ticketService = new TicketService(subject);
        this.dictionariesService = new DictionariesService();

        this.id = null;
        this.ticketType = TicketTypes.EGE;
        this.egeTaskCount = 0;
        this.thematicTaskCount = 5;
        this.name = '';
        this.taskCount = 0;
        this.currentTaskCount = 0;
        this.tasks = [];
        this.loading = false;
        this.pdfLink = null;
        this.parentId = null;
        this.parentTasks = [];
        this.checkedParentTaskUuids = new Set<string>();

        makeAutoObservable(this);
    }

    loadFull(ticketId?: number | null | undefined): void {

        this.dictionariesService.getActiveNumberInKim(this.subject).then(
            (data) => {
                this.egeTaskCount = (data as []).length;
                this.taskCount = this.ticketType === TicketTypes.EGE ? this.egeTaskCount : this.thematicTaskCount;

                if (ticketId) {
                    this.loadFullTicket(ticketId);
                } else {

                    this.ticketService.loadCurrent(
                        data => {
                            if (data?.id) {
                                this.loadFullTicket(data.id);
                            } else {
                                this.taskCount = this.ticketType === TicketTypes.EGE ? this.egeTaskCount : this.thematicTaskCount;
                            }
                        }
                    );
                }
            }
        )
    }

    private loadFullTicket(ticketId: number) {
        try {
            this.ticketService.loadFull(
                ticketId,
                (data) => {
                    if (data?.id) {
                        this.id = data['id'];
                        this.ticketType = data['ticketType'];
                        this.name = data['name'];
                        this.taskCount = data['taskCount'];
                        this.currentTaskCount = data['currentTaskCount'];
                        this.tasks = data['tasks'];
                        this.pdfLink = data['pdf'] ? this.filesService.getFileUrl(data['pdf']) : null;

                        if (this.ticketType === TicketTypes.THEMATIC) {
                            this.thematicTaskCount = this.taskCount;
                        }

                        const parentId = data['parent'];

                        if (parentId) {
                            this.checkedParentTaskUuids = new Set<string>(this.tasks.map(task => task.uuid));
                            this.ticketService.loadFull(parentId, (data) => { 
                                this.parentId = data['id'];
                                this.parentTasks = (data['tasks'] as ITask[])
                                    .map(task => new TaskItemStore(task, this.subject, this.taskService, this.checkedParentTaskUuids.has(task.uuid)))
                            });
                        }
                    }
                }
            );
        } catch (error) {
            runInAction(() => { message.error(error); });
        }
    }

    getTasksAsSteps(): StepProps[] {

        if (this.ticketType === TicketTypes.EGE) {

            return this.range(0, this.egeTaskCount)
                .map((indx) => {

                    const task = this.tasks.find(t => t.numberInKim.name === String(indx));
                    if (task) {
                        return {
                            status: "process",
                            title: `${task.numberInKim.name}. Задача №${task.id}`,
                            description: React.createElement(NewTicketTaskItem, { task, subject: this.subject })
                        } as StepProps;
                    } else {
                        return { status: "wait", title: `${indx}.` } as StepProps;
                    }
                });

        } else {

            const mappedTasks = this.mapTasksToStepProps(this.tasks);
            const emptyTasks = mappedTasks.length === this.thematicTaskCount ? [] : this.range(mappedTasks.length, this.thematicTaskCount)
                .map((indx) => { return { status: "wait", title: `${indx}.` } as StepProps });

            return [...mappedTasks, ...emptyTasks];
        }
    }

    getParentTaskAsSteps(): StepProps[] {

        if (this.parentTasks) {
            return this.parentTasks.map((task) => {
                return {
                    status: "process",
                    title: `${task.data.numberInKim.name}. Задача №${task.data.id}`,
                    description: React.createElement(NewTicketTaskClonesItem, { task: task, subject: this.subject })
                } as StepProps;
            })
        } else {
            return [];
        }
    }

    range(start: number, end: number): number[] {
        return end < start ? [] : Array.from(Array(end - start).keys()).map(x => x + start + 1);
    }

    mapTasksToStepProps(tasks: ITask[]): StepProps[] {
        return tasks?.slice().sort((t1, t2) => t1.id - t2.id).map((t, indx) => {
            return {
                status: "process",
                title: `${indx + 1}. Задача №${t.id}`,
                description: React.createElement(NewTicketTaskItem, { task: t, subject: this.subject })
            } as StepProps
        });
    }

    createNewTicket(mathTicketBar: TicketBar) {

        this.loading = true;

        try {

            const params: NewTicketRequest = {
                ticketType: this.ticketType,
                count: this.taskCount,
                name: this.name
            }

            this.ticketService.createTicket(
                params,
                data => {
                    if (data?.id) {
                        this.id = data.id;
                        mathTicketBar.update(data.id, 0, data.taskCount);
                    }

                    this.loading = false;
                },
                (error: string) => {
                    this.loading = false;
                    message.error(`Ошибка при создании варианта: ${error}`);
                }
            );
        } catch (error) {
            runInAction(() => {
                this.loading = false;
                message.error(`Ошибка при создании варианта: ${error}`);
            });
        }
    }

    reset = async () => {

        if (this.id) {
            this.loading = true;

            try {

                this.ticketService.reset(
                    this.id,
                    () => {
                        this.currentTaskCount = 0;
                        this.tasks = [];
                        this.loading = false;
                    },
                    (error: string) => {
                        this.loading = false;
                        message.error(`Ошибка при сбросе данных: ${error}`);
                    }
                );
            } catch (error) {
                runInAction(() => {
                    this.loading = false;
                    message.error(`Ошибка при сбросе данных: ${error}`);
                });
            }
        }
    }

    finish = async () => {

        if (this.id) {
            this.loading = true;

            if (this.currentTaskCount !== 0 && this.ticketType === TicketTypes.EGE && this.currentTaskCount !== this.egeTaskCount) {
                message.warning('Ещё не добавлены все задачи для ЕГЭ!');
                return;
            }

            try {

                const params: FinishTicketRequest = {
                    count: this.taskCount,
                    name: this.name
                }

                this.ticketService.finish(
                    this.id,
                    params,
                    () => {
                        this.id = null;
                        this.currentTaskCount = 0;
                        this.taskCount = 5;
                        this.tasks = [];
                        this.loading = false;
                        this.name = '';

                        message.success('Формирование варианта успешно завершено');
                    },
                    (error: string) => {
                        this.loading = false;
                        message.error(`Ошибка при завершении формирования варианта: ${error}`);
                    }
                );
            } catch (error) {
                runInAction(() => {
                    this.loading = false;
                    message.error(`Ошибка при завершении формирования варианта: ${error}`);
                });
            }
        }
    }

    changeTicketType(type: TicketTypes): void {

        this.ticketType = type;

        if (type === TicketTypes.EGE) {
            this.taskCount = this.egeTaskCount;
        } else {
            this.taskCount = this.thematicTaskCount;
        }
    }

    changeThematicCount(count: number): void {

        if (count >= this.currentTaskCount) {
            this.thematicTaskCount = count;
            this.taskCount = count;
        } else {
            message.error("Нельзя установить значение меньше количества добавленных задач");
        }
    }

    changeName(value: string) {
        this.name = value;
    }

    contains(id: number): boolean {
        return this.tasks.some(t => t.id === id);
    }

    toggleTaskInTicket(taskId: number, ticketBar: TicketBar): void {

        if (this.contains(taskId)) {
            this.deleteTaskFromList(taskId, ticketBar);
        } else {
            this.addTaskInList(taskId, ticketBar);
        }
    }

    addTaskInList(taskId: number, ticketBar: TicketBar): void {

        if (this.id) {
            this.loading = true;
            try {
                this.ticketService.addTaskInList(
                    this.id, taskId,
                    (data: any) => {
                        const task = data.task as ITask;
                        this.tasks.push(task);
                        this.checkedParentTaskUuids.add(task.uuid);
                        this.currentTaskCount += 1;

                        this.updateCheckedTasks();
                        ticketBar.update(this.id, this.currentTaskCount, this.taskCount);
                    },
                    (error: string) => {
                        this.loading = false;
                        message.error(`Ошибка при добавлении задачи в вариант: ${error}`);
                    }
                );
            } catch (error) {
                runInAction(() => {
                    this.loading = false;
                    message.error(`Ошибка при добавлении задачи в вариант: ${error}`);
                });
            }
        }
    }

    deleteTaskFromList(taskId: number, ticketBar: TicketBar): void {

        if (this.id) {
            this.loading = true;
            try {
                this.ticketService.deleteTaskInList(
                    this.id, taskId,
                    () => {
                        const task = this.tasks.find(t => t.id === taskId);
                        this.tasks = this.tasks.filter(t => t.id !== taskId);
                        this.currentTaskCount -= 1;
                        if (task) {
                            this.checkedParentTaskUuids.delete(task.uuid);
                            this.updateCheckedTasks();
                        }
                        ticketBar.update(this.id, this.currentTaskCount, this.taskCount);
                    },
                    (error: string) => {
                        this.loading = false;
                        message.error(`Ошибка при удалении задачи из варианта: ${error}`);
                    }
                );
            } catch (error) {
                runInAction(() => {
                    this.loading = false;
                    message.error(`Ошибка при удалении задачи из варианта: ${error}`);
                });
            }
        }
    }

    updateCheckedTasks() {
        this.parentTasks.forEach(task => task.changeChosenClone(this.checkedParentTaskUuids));
    }

    makePdf(): void {
        if (this.id) {
            this.loading = true;
            try {
                this.ticketService.makePdf(
                    this.id,
                    data => {
                        this.loading = false;
                        this.pdfLink = this.filesService.getFileUrl(data["id"]);
                    },
                    (error: string) => {
                        this.loading = false;
                        message.error(`Ошибка при формировании PDF: ${error}`);
                    }
                );
            } catch (error) {
                runInAction(() => {
                    this.loading = false;
                    message.error(`Ошибка при формировании PDF: ${error}`);
                });
            }
        }
    }

    regeneratePdf(): void {
        if (this.id) {
            this.loading = true;
            try {
                this.ticketService.regeneratePdf(
                    this.id,
                    data => {
                        this.loading = false;
                        this.pdfLink = this.filesService.getFileUrl(data["id"]);
                        message.success('PDF успешно перегенерирован');
                    },
                    (error: string) => {
                        this.loading = false;
                        message.error(`Ошибка при формировании PDF: ${error}`);
                    }
                );
            } catch (error) {
                runInAction(() => {
                    this.loading = false;
                    message.error(`Ошибка при формировании PDF: ${error}`);
                });
            }
        }
    }
}